import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import submit from "submit-form";

import NavigationComponent from "../../calendar/components/NavigationComponent";
import { Footer, ListView, CalendarView, Header } from "./parts";
import { AthleteLinks } from "../../../../parts/athlete-links";
import { AthleteSelect } from "../../../../parts/inputs/athlete-select-new";

import {
  useDailyStats,
  useAthleteVolumes,
  useDatesAttachments,
  useDateSelector,
  usePhaseSelection,
} from "../../../../hooks/annual-planner";

import {
  getDefaultPhases,
  getFooterData,
  getSortedWeekDays,
} from "../../../../utils/annual-planner-helpers";
import { whoIsAthleteCoach } from "../../../../utils/user-helpers";
import getAvatarUrl from "../../../../utils/get-avatar-url";

export const AnnualPlanner = ({ locals }) => {
  const {
    query,
    pathname,
    activeUser,
    phasesTemplates,
    dailyStats,
    athleteVolumes,
    athlete,
    coachOrganizationInformation,
  } = locals;

  const athletePhases = athlete.annualPlan?.trainingPhases || [];

  const startingCalendarDay =
    coachOrganizationInformation?.startingCalendarDay ||
    activeUser.startingCalendarDay ||
    0;

  const organizationCoach = query.organizationCoach || undefined;
  const organizationCoachId = organizationCoach
    ? whoIsAthleteCoach(
        activeUser.athletes,
        query.athlete,
        activeUser._id,
        organizationCoach
      )
    : undefined;

  const weekDays = getSortedWeekDays(startingCalendarDay);
  const defaultPhases = getDefaultPhases(phasesTemplates);

  const { from, to, athleteSwitch, changedTemplate, statsFrom, prevFrom } =
    query;
  const {
    datesAttachments = [],
    phases = defaultPhases,
    phasesDescriptions = [],
  } = athletePhases;

  const { units } = activeUser;

  const [reloading, setReloading] = useState(false);
  const { athleteStatsState } = useDailyStats({ dailyStats, prevFrom });
  const { athleteVolumesState } = useAthleteVolumes({
    prevFrom,
    athleteVolumes,
  });
  const { phasesAttachments, setPhasesAttachments } = useDatesAttachments({
    athleteSwitch,
    changedTemplate,
    datesAttachments,
    setReloading,
  });
  const { dateFromState, setDateFromState } = useDateSelector({
    from,
    startingCalendarDay,
    statsFrom,
  });

  const calendarEvents =
    athlete.annualPlan?.events?.filter((event) => {
      const eventStart = moment(event.fromDate, "YYYYMMDD");
      const startOfCurrentMonth = moment(dateFromState).startOf("month");
      if (moment(eventStart).isSameOrAfter(startOfCurrentMonth)) return event;
    }) || [];

  const [dateToState, setDateToState] = useState(to);
  const [horizontalScrollRef, setHorizontalScrollRef] = useState(null);
  const [listView, setListView] = useState(false);
  const [selectedDaysState, setSelectedDaysState] = useState([]);
  const annualPlannerMainRef = useRef();

  const { handlePhaseSelection } = usePhaseSelection(
    phasesAttachments,
    setPhasesAttachments,
    athlete,
    reloading,
    setReloading,
    organizationCoachId
  );

  const athleteStatsHeaders = [
    "Readiness",
    "Session RPE",
    "Weekly Volume",
    `Body Weight ${units === "metric" ? "(Kg)" : "(lbs)"}`,
    "Daily kCal",
    "Steps",
  ];

  useEffect(() => {
    // We just refresh the page to clean the params if the cleaning failed in the
    // annual-planner-controller.js - See that file for better understanding
    if (
      window &&
      String(window.performance.getEntriesByType("navigation")[0].type) ===
        "reload" &&
      prevFrom
    ) {
      submit("/actions/refresh", {
        method: "GET",
        body: {
          statsFrom: "",
          prevFrom: "",
          changedTemplate: "",
        },
      });
    }
  }, []);

  const {
    upcomingEvents,
    nextEvent,
    upcomingComments,
    firstPhaseDescription,
    secondPhaseDescription,
  } = getFooterData(calendarEvents, phasesDescriptions);

  return (
    <main role="reports-wrapper">
      <div className="athlete-section-header only-show-mobile">
        <AthleteLinks
          athlete={athlete}
          pathName={pathname}
          classNames={"only-show-mobile"}
          query={query}
        />
        <div className="athlete-select dropdown-athlete">
          <div
            className="avatar"
            style={{
              backgroundImage: `url(${getAvatarUrl(locals?.athlete?._id)})`,
            }}
          />
          <AthleteSelect locals={locals} athleteSwitch={true} />
        </div>
      </div>

      <NavigationComponent
        query={query}
        text="Calendar Day View"
        pathName={pathname}
        classNames="mobile-hidden"
      />
      <article
        id="annual-planner"
        role="report-content"
        ref={annualPlannerMainRef}
      >
        <Header
          listView={listView}
          setListView={setListView}
          dateFromState={dateFromState}
          setDateFromState={setDateFromState}
          dateToState={dateToState}
          setDateToState={setDateToState}
          selectedDaysState={selectedDaysState}
          setSelectedDaysState={setSelectedDaysState}
          phasesTemplates={phasesTemplates}
          locals={locals}
        />

        {!listView ? (
          <section role="planner-wrapper" className={`mobile-hidden`}>
            <CalendarView
              weekDays={weekDays}
              phases={phases}
              calendarEvents={calendarEvents}
              athleteStatsHeaders={athleteStatsHeaders}
              dateFromState={dateFromState}
              startingCalendarDay={startingCalendarDay}
              selectedDaysState={selectedDaysState}
              setSelectedDaysState={setSelectedDaysState}
              phasesAttachments={phasesAttachments}
              setPhasesAttachments={setPhasesAttachments}
              handlePhaseSelection={handlePhaseSelection}
              phasesDescriptions={phasesDescriptions}
              setHorizontalScrollRef={setHorizontalScrollRef}
              athleteStatsState={athleteStatsState}
              athleteVolumesState={athleteVolumesState}
            />
          </section>
        ) : (
          //     List View
          <ListView
            dateFromState={dateFromState}
            startingCalendarDay={startingCalendarDay}
            calendarEvents={calendarEvents}
            datesAttachments={datesAttachments}
            phasesDescriptions={phasesDescriptions}
            athleteStatsState={athleteStatsState}
            athleteVolumesState={athleteVolumesState}
            classNames="mobile-hidden"
          />
        )}
        {/* List View */}
        <ListView
          dateFromState={dateFromState}
          startingCalendarDay={startingCalendarDay}
          calendarEvents={calendarEvents}
          datesAttachments={datesAttachments}
          phasesDescriptions={phasesDescriptions}
          athleteStatsState={athleteStatsState}
          athleteVolumesState={athleteVolumesState}
          classNames="only-show-mobile"
        />

        {!listView && (
          //   Events, comments & other
          <Footer
            nextEvent={nextEvent}
            upcomingEvents={upcomingEvents}
            upcomingComments={upcomingComments}
            firstPhaseDescription={firstPhaseDescription}
            secondPhaseDescription={secondPhaseDescription}
            setDateFromState={setDateFromState}
            setDateToState={setDateToState}
            horizontalScrollRef={horizontalScrollRef}
            annualPlannerMainRef={annualPlannerMainRef}
          />
        )}
      </article>
    </main>
  );
};
